import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Printer, Loader2, BookOpen } from "lucide-react";
import { getPublicQuote, respondPublicQuote, formatUSD } from "@/lib/api";
import { getT } from "@/lib/i18n";

const Pair = ({ label, value }) => (
  <div className="border border-[#e5e5e5] p-4">
    <div className="text-label mb-2">{label}</div>
    <div className="font-mono text-sm">{value || <span className="text-[#525252]">—</span>}</div>
  </div>
);

export default function PublicQuote() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [lang, setLang] = useState("en");

  const load = () =>
    getPublicQuote(token)
      .then((data) => {
        setQ(data);
        setLang(data?.language === "fr" ? "fr" : "en");
      })
      .catch(() => setQ(null))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, [token]);

  const t = getT(lang);

  const respond = async (status) => {
    setResponding(true);
    try {
      await respondPublicQuote(token, status);
      toast.success(status === "accepted" ? t.accepted_msg : t.declined_msg);
      load();
    } catch {
      toast.error("Failed to submit response");
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-sm uppercase tracking-[0.2em] text-[#525252]">{getT("en").loading}</div>
        </div>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center max-w-md">
          <BookOpen className="w-10 h-10 mx-auto mb-4 text-[#050505]" strokeWidth={1.5} />
          <h1 className="font-display font-black text-3xl tracking-tighter mb-3">{t.quote_not_found}</h1>
          <p className="text-sm text-[#525252]">{t.quote_not_found_sub}</p>
        </div>
      </div>
    );
  }

  const b = q.breakdown;
  const s = q.spec;
  const decided = q.status === "accepted" || q.status === "declined";

  return (
    <div className="min-h-screen bg-white" data-testid="public-quote-page">
      {/* Lean public header */}
      <header className="no-print border-b border-[#050505]">
        <div className="max-w-screen-lg mx-auto px-6 sm:px-10 py-5 flex items-center justify-between gap-4 flex-wrap">
          <a href="/" className="flex items-center" data-testid="public-brand">
            <img src="/logo.png" alt="2RA-press" className="h-12 sm:h-14 w-auto" />
          </a>
          <div className="flex items-center gap-2">
            {/* Language toggle */}
            <div className="flex" data-testid="public-lang-toggle">
              {[
                { code: "en", label: "EN" },
                { code: "fr", label: "FR" },
              ].map((opt) => (
                <button
                  key={opt.code}
                  onClick={() => setLang(opt.code)}
                  data-testid={`public-lang-${opt.code}`}
                  className={`px-3 h-9 text-[11px] uppercase tracking-[0.18em] font-bold border border-[#050505] transition-colors ${
                    lang === opt.code
                      ? "bg-[#050505] text-white"
                      : "bg-white text-[#050505] hover:bg-[#f5f5f5]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button onClick={() => window.print()} className="btn-secondary" data-testid="public-print-btn">
              <Printer className="w-4 h-4" strokeWidth={1.5} /> {t.save_pdf}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-lg mx-auto px-6 sm:px-10 py-10 lg:py-16">
        {/* Headline */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#050505]">
          <div className="lg:col-span-8 p-6 lg:p-12 lg:border-r border-[#050505]">
            <div className="text-label mb-3">{t.your_quote}</div>
            <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none" data-testid="public-quote-number">
              {q.quote_number}
            </div>
            <div className="mt-3 text-sm text-[#525252]">
              {t.issued}{" "}
              <span className="font-mono">
                {new Date(q.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
              {" · "}
              <span className={`status-pill status-${q.status} align-middle`} data-testid="public-status">
                {t.status[q.status] || q.status}
              </span>
            </div>

            {q.project_title && (
              <div className="mt-8">
                <div className="text-label mb-2">{t.project}</div>
                <div className="font-display text-2xl font-bold" data-testid="public-project">{q.project_title}</div>
              </div>
            )}
            {q.client_name && (
              <div className="mt-6">
                <div className="text-label mb-2">{t.prepared_for}</div>
                <div className="font-display text-lg font-bold">{q.client_name}</div>
                {q.client_email && (
                  <div className="font-mono text-xs text-[#525252] mt-1">{q.client_email}</div>
                )}
              </div>
            )}
            {q.notes && (
              <div className="mt-6 text-sm text-[#525252] leading-relaxed border-l-2 border-[#050505] pl-4">
                {q.notes}
              </div>
            )}
          </div>
          <div className="lg:col-span-4 p-6 lg:p-12 bg-[#F5F5F5] flex flex-col">
            <div className="text-label mb-3">{t.total}</div>
            <div className="font-mono font-black text-5xl tracking-tighter leading-none text-[#FF3333]" data-testid="public-total">
              {formatUSD(b.total)}
            </div>
            <div className="mt-4 font-mono text-sm text-[#525252]">
              <span data-testid="public-unit-price">{formatUSD(b.unit_price)}</span> {t.per_book}
              <br />
              <span data-testid="public-qty">{b.quantity}</span> {t.copies}
            </div>
            {b.tax_rate > 0 && (
              <div className="mt-3 text-[11px] uppercase tracking-[0.18em] text-[#525252]">
                {t.includes_tax(b.tax_rate, formatUSD(b.tax_amount))}
              </div>
            )}
            <div className="mt-auto pt-8 text-[11px] uppercase tracking-[0.2em] text-[#525252]">
              {t.all_prices_in} {b.currency} · {t.valid_30_days}
            </div>
          </div>
        </div>

        {/* Spec grid */}
        <section className="mt-px border border-[#050505] border-t-0">
          <div className="px-6 lg:px-12 py-5 border-b border-[#050505]">
            <h2 className="font-display font-bold text-lg uppercase tracking-tight">{t.specifications}</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 -ml-px -mt-px">
            <Pair label={t.trim_size} value={q.labels.trim_size} />
            <Pair label={t.page_count} value={`${s.page_count} ${t.pp}`} />
            <Pair label={t.interior_color} value={q.labels.color_mode} />
            <Pair label={t.interior_paper} value={q.labels.paper_type} />
            <Pair label={t.cover} value={q.labels.cover_type} />
            <Pair label={t.binding} value={q.labels.binding_type} />
            <Pair label={t.finishing} value={q.labels.finishing_type} />
            <Pair label={t.quantity} value={t.qty_copies(s.quantity)} />
          </div>
        </section>

        {/* Accept / Decline */}
        <section className="no-print mt-10 border border-[#050505] p-6 lg:p-10" data-testid="public-decision">
          {decided ? (
            <div className="text-center">
              <div className="font-display font-black text-2xl tracking-tighter mb-2">
                {q.status === "accepted" ? t.quote_accepted : t.quote_declined}
              </div>
              <div className="text-sm text-[#525252]">
                {q.status === "accepted" ? t.accepted_msg : t.declined_msg}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] items-center gap-4">
              <div>
                <div className="font-display font-bold text-xl tracking-tight">{t.ready_to_proceed}</div>
                <div className="text-sm text-[#525252] mt-1">{t.ready_subtitle}</div>
              </div>
              <button
                onClick={() => respond("declined")}
                disabled={responding}
                className="btn-secondary"
                data-testid="public-decline-btn"
              >
                <XCircle className="w-4 h-4" strokeWidth={1.5} /> {t.decline}
              </button>
              <button
                onClick={() => respond("accepted")}
                disabled={responding}
                className="btn-primary"
                data-testid="public-accept-btn"
              >
                {responding ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />}
                {t.accept_quote}
              </button>
            </div>
          )}
        </section>

        <footer className="mt-12 text-[11px] uppercase tracking-[0.2em] text-[#525252] text-center">
          {t.quote_footer(q.quote_number)}
        </footer>
      </main>
    </div>
  );
}
